import React, { useCallback, useMemo, useRef, useState } from "react";
import { Icon, Tooltip, Modal, Popconfirm } from "antd";
import { useTranslation } from "react-i18next";

import QuestionForm from "./forms/QuestionForm";
import OptionForm from "./forms/OptionForm";
import MessageForm from "./forms/MessageForm";
import AnswerForm from "./forms/AnswerForm";
import { ANSWER_TYPES } from "./forms/QuestionForm/QuestionForm";

import styled from "styled-components/macro";

const Block = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin-top: -1px;
`;

const Row = styled.div`
  display: flex;
  min-height: 64px;
  justify-content: center;
  border-top: none;
  padding-bottom: 20px;
  position: relative;
  &:before {
    content: "";
    height: 31px;
    width: 2px;
    position: absolute;
    top: -31px;
    background: #e0e0e0;
    margin-left: -33px;
  }

  ${Block}:before {
    content: "";
    display: block;
    width: 100%;
    border-bottom: 2px solid #e0e0e0;
  }

  ${Block}:first-child:before {
    margin-left: auto;
    width: calc(50% + 16px);
  }

  ${Block}:last-child:before {
    margin-right: auto;
    width: calc(50% - 16px);
  }
  ${Block}:only-child:before {
    content: none;
  }
`;

const MessageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  position: relative;
  margin: 24px 0;
  button {
    margin-left: 12px;
  }
  &:after {
    content: "";
    height: 32px;
    width: 2px;
    background: none;
    position: absolute;
    top: -24px;
    background: #e0e0e0;
    margin-left: -33px;
  }
  &:before {
    content: "";
    width: 10px;
    height: 10px;
    display: inline-flex;
    background: ${({ theme }) => theme["@grey-darker"]};
    border-radius: 50%;
    position: absolute;
    top: -12px;
    z-index: 1;
    margin-left: -33px;
  }
  &:last-of-type {
    border-bottom: none;
  }
`;

const Answer = styled.span`
  margin-left: 50px;
  padding: 6px 12px 6px 26px;
  font-size: 1em;
  line-height: 1.5;
  display: inline-flex;
  position: relative;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  color: ${({ theme }) => theme["@primary"]};
  background: white;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.15);
  transition: 0.3s ease;
  max-width: 320px;
  min-width: 160px;
  min-height: 36px;

  &:hover {
    box-shadow: 0px 8px 15px -3px gainsboro;
  }

  .user-icon {
    position: absolute;
    left: -18px;
    width: 36px;
    height: 36px;
    background-color: ${({ theme }) => theme["@primary"]};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    margin-right: 12px;
  }

  .text {
    text-align: center;
  }
`;

const AnswerContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  margin-top: 30px;

  &:after {
    content: "";
    height: 31px;
    width: 2px;
    position: absolute;
    top: -1px;
    background: #e0e0e0;
    margin-left: -33px;
  }
  &:before {
    content: "";
    width: 10px;
    height: 10px;
    display: inline-flex;
    background: ${({ theme }) => theme["@primary"]};
    border-radius: 50%;
    position: absolute;
    top: 10px;
    z-index: 1;
    margin-left: -33px;
  }
`;

const IconButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  opacity: ${props => (props.visible ? 1 : 0)};
  transform: ${props =>
    !props.visible ? "translateY(10px)" : "translateY(0px)"};
  transition: 0.3s ease;
  font-size: 1em;
  margin: ${props => props.visible && "0 auto"};
  margin-left: ${props => props.visible && "calc(50% - 47px)"};
  padding: 0;
  &:active,
  &:focus {
    outline: none;
  }
  &:nth-child(2) {
    transition-delay: 0.1s;
  }
  &:nth-child(3) {
    transition-delay: 0.2s;
  }
  ${MessageContainer}:hover &,
  ${AnswerContainer}:hover & {
    transform: translateY(0);
    opacity: 1;
  }
  i {
    padding: 8px;
    transition: 0.3s ease;
    border-radius: 50%;
    background: ${({ theme, disabled }) =>
      theme[disabled ? "@gray-40" : "@gray-20"]};
    color: ${({ theme, disabled }) => (disabled ? theme["@white"] : "inherit")};
    &:hover {
      background: ${({ theme, disabled }) =>
        theme[disabled ? "@gray-40" : "@primary"]};
      color: ${({ theme }) => theme["@white"]};
    }
  }
`;

const Message = styled.span`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 6px 12px 6px 26px;
  font-weight: bold;
  line-height: 1.5;
  margin-left: 50px;
  color: ${({ theme }) => theme["@grey-darker"]};
  background: white;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.15);
  transition: 0.3s ease;
  min-width: 160px;
  max-width: 320px;
  min-height: 36px;

  .ant-input:hover {
    border-color: inherit;
  }
  &:hover {
    box-shadow: 0px 8px 15px -3px gainsboro;
  }

  .robot-icon {
    position: absolute;
    left: -18px;
    width: 36px;
    height: 36px;
    background-color: ${({ theme }) => theme["@grey-darker"]};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    margin-right: 12px;
  }

  .text {
    text-align: center;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  min-width: 120px;
  line-height: 1.5;
`;

const TagBadge = styled.div`
  width: 18px;
  height: 18px;
  position: absolute;
  right: -9px;
  top: -9px;
  border-radius: 50%;
  font-size: 16px;
`;

const Tree = props => {
  const {
    t,
    id,
    tags,
    message,
    children,
    parent,
    map,
    onAddOption,
    onRemoveOption,
    onRemoveBranch,
    onEditMessage,
    onEditAnswer,
    onEditMultiple,
    isRoot = false,
    type
  } = props;

  const tooltipContainer = useRef(null);
  const [modal, setModal] = useState(null);
  const [branchVisibility, setBranchVisibility] = useState(false);
  const [messageVisibility, setMessageVisibility] = useState(false);
  const [answerVisibility, setAnswerVisibility] = useState(false);
  const [deleteVisibility, setDeleteVisibility] = useState(false);

  const closeModal = useCallback(event => {
    if (event) event.stopPropagation();

    setModal(null);
  }, []);

  const handleSubmit = useCallback(
    values => {
      const { options, ...other } = values;
      onEditMultiple(id, { values: options, ...other });
    },
    [id, onEditMultiple]
  );

  const relevantParent = useMemo(() => parent && map[parent.id], [map, parent]);

  const isOptionsAnswer = useMemo(
    () => type && type === ANSWER_TYPES.options.type,
    [type]
  );

  const hasOptionsParent = useMemo(
    () => relevantParent && relevantParent.type === ANSWER_TYPES.options.type,
    [relevantParent]
  );
  const hasYesNoParent = useMemo(
    () => relevantParent && relevantParent.type === ANSWER_TYPES.yes_no.type,
    [relevantParent]
  );

  const answers = { Si: t("global:yes"), No: t("global:no") };

  return (
    <Block key={id} id={id} data-test="tree" ref={tooltipContainer}>
      {parent && (
        <AnswerContainer>
          <Answer>
            <Icon className="user-icon" type="user" />
            <span className="text" data-test="answer">
              {hasYesNoParent ? answers[parent.answer] : parent.answer}
            </span>
            <TagBadge>
              <Tooltip
                getPopupContainer={() => tooltipContainer.current}
                placement="top"
                title={parent.tag}
              >
                <Icon
                  type="tag"
                  style={{
                    opacity: parent.tag ? 1 : 0.4
                  }}
                  theme={parent.tag ? "filled" : "outlined"}
                />
              </Tooltip>
            </TagBadge>
          </Answer>
          <ButtonContainer>
            <Tooltip
              visible={answerVisibility}
              getPopupContainer={() => tooltipContainer.current}
              onVisibleChange={() => setAnswerVisibility(!answerVisibility)}
              placement="top"
              title={t(
                `opportunity:process.stepTypes.chatbot.tree.${
                  hasOptionsParent ? "editAnswerAndTag" : "editTag"
                }`
              )}
            >
              <IconButton
                data-test="editAnswer"
                type="button"
                style={{ marginLeft: "12px" }}
                onClick={() => {
                  setModal("editAnswer");
                  setAnswerVisibility(false);
                }}
              >
                <Icon type="edit" />
                <Modal
                  destroyOnClose
                  title={t(
                    "opportunity:process.stepTypes.chatbot.tree.editAnswer"
                  )}
                  visible={modal === "editAnswer"}
                  footer={null}
                  onCancel={closeModal}
                >
                  <AnswerForm
                    t={t}
                    initialValues={parent}
                    tags={tags}
                    admitTags={true}
                    canEditAnswer={hasOptionsParent}
                    answerType={relevantParent && relevantParent.type}
                    onSubmit={({ answer, tag }) =>
                      onEditAnswer(parent.id, id, answer, tag)
                    }
                    onClose={closeModal}
                  />
                </Modal>
              </IconButton>
            </Tooltip>
          </ButtonContainer>
        </AnswerContainer>
      )}
      <MessageContainer>
        <Message>
          <Icon className="robot-icon" type="robot" />
          <span className="text" data-test="message">
            {message}
          </span>
        </Message>
        <ButtonContainer>
          <Tooltip
            visible={messageVisibility}
            getPopupContainer={() => tooltipContainer.current}
            onVisibleChange={() => setMessageVisibility(!messageVisibility)}
            placement="top"
            title={t("opportunity:process.stepTypes.chatbot.tree.editMessage")}
          >
            <IconButton
              data-test="editMessage"
              type="button"
              onClick={() => {
                setModal("editMessage");
                setMessageVisibility(false);
              }}
            >
              <Icon type="edit" />
              <Modal
                title={t(
                  "opportunity:process.stepTypes.chatbot.tree.editMessage"
                )}
                visible={modal === "editMessage"}
                footer={null}
                onCancel={closeModal}
              >
                <MessageForm
                  t={t}
                  initialValue={message}
                  onSubmit={message => onEditMessage(id, message)}
                  onClose={closeModal}
                />
              </Modal>
            </IconButton>
          </Tooltip>
          {children && (
            <Tooltip
              visible={branchVisibility}
              getPopupContainer={() => tooltipContainer.current}
              onVisibleChange={() => setBranchVisibility(!branchVisibility)}
              placement="top"
              title={t("opportunity:process.stepTypes.chatbot.tree.addOption")}
            >
              <IconButton
                data-test="addOption"
                type="button"
                disabled={!isOptionsAnswer}
                onClick={() => {
                  setModal("addOption");
                  setBranchVisibility(false);
                }}
              >
                <Icon type="plus" />
                <Modal
                  destroyOnClose
                  title={t(
                    "opportunity:process.stepTypes.chatbot.tree.addOption"
                  )}
                  visible={modal === "addOption"}
                  footer={null}
                  onCancel={closeModal}
                >
                  <OptionForm
                    t={t}
                    onSubmit={values => onAddOption(id, values)}
                    onClose={closeModal}
                  />
                </Modal>
              </IconButton>
            </Tooltip>
          )}
          {!isRoot &&
            (children ? (
              <Popconfirm
                title={t(
                  "opportunity:process.stepTypes.chatbot.tree.removeBranchConfirmation"
                )}
                okText={t(
                  "opportunity:process.stepTypes.chatbot.tree.removeBranch"
                )}
                cancelText={t("global:cancel")}
                onConfirm={() => onRemoveBranch(id)}
                getPopupContainer={() => tooltipContainer.current}
              >
                <Tooltip
                  visible={deleteVisibility}
                  getPopupContainer={() => tooltipContainer.current}
                  onVisibleChange={() => setDeleteVisibility(!deleteVisibility)}
                  placement="top"
                  title={t(
                    "opportunity:process.stepTypes.chatbot.tree.removeBranch"
                  )}
                >
                  <IconButton
                    type="button"
                    onClick={() => setDeleteVisibility(!deleteVisibility)}
                  >
                    <Icon type="close" />
                  </IconButton>
                </Tooltip>
              </Popconfirm>
            ) : (
              <Popconfirm
                title={t(
                  "opportunity:process.stepTypes.chatbot.tree.removeAnswerConfirmation"
                )}
                okText={t(
                  "opportunity:process.stepTypes.chatbot.tree.removeAnswer"
                )}
                okButtonProps={{
                  "data-test": "removeOptionBtnConfirm"
                }}
                cancelText={t("global:cancel")}
                onConfirm={() => onRemoveOption(id)}
              >
                <Tooltip
                  visible={deleteVisibility}
                  getPopupContainer={() => tooltipContainer.current}
                  onVisibleChange={() => setDeleteVisibility(!deleteVisibility)}
                  placement="top"
                  title={t(
                    "opportunity:process.stepTypes.chatbot.tree.removeOption"
                  )}
                >
                  <IconButton
                    data-test="removeOptionBtn"
                    type="button"
                    disabled={!hasOptionsParent}
                    onClick={() => setDeleteVisibility(!deleteVisibility)}
                  >
                    <Icon type="delete" />
                  </IconButton>
                </Tooltip>
              </Popconfirm>
            ))}
        </ButtonContainer>
      </MessageContainer>
      {children ? (
        <Row>
          {children.map(child => (
            <Tree
              {...props}
              isRoot={false}
              key={child.next_id}
              parent={{ id, ...child }}
              {...map[child.next_id]}
            />
          ))}
        </Row>
      ) : (
        <Tooltip
          visible={branchVisibility}
          getPopupContainer={() => tooltipContainer.current}
          onVisibleChange={() => setBranchVisibility(!branchVisibility)}
          placement="top"
          title={t("opportunity:process.stepTypes.chatbot.tree.addBranch")}
        >
          <IconButton
            type="button"
            data-test="add-branch"
            visible
            onClick={() => {
              setModal("editQuestion");
              setBranchVisibility(false);
            }}
          >
            <Icon type="branches" />
          </IconButton>
        </Tooltip>
      )}
      <Modal
        destroyOnClose
        visible={modal === "editQuestion"}
        footer={null}
        onCancel={closeModal}
        width={740}
      >
        <QuestionForm
          t={t}
          message={message}
          onSubmit={handleSubmit}
          onClose={closeModal}
        />
      </Modal>
    </Block>
  );
};

const Workflow = ({ data, ...props }) => {
  const { t } = useTranslation(["opportunity", "global"]);

  const root = data.find(workflow => workflow.root);
  const workflows = Object.values(data).reduce(
    (acc, item) => (acc[item.id] ? acc : { ...acc, [item.id]: item }),
    {}
  );

  return <Tree map={workflows} {...props} {...root} isRoot={true} t={t} />;
};

export default Workflow;
